import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fieldErrorsFrom422, formatApiErrorDetail } from "@/lib/api";
import { FormField, adminInput } from "./shared";

export const TRACKER_STATUSES = ["Active", "In development", "Parked", "For sale", "Paused"];
const BLANK = { domain: "", registrar: "", renewal_date: "", hosting: "", monthly_revenue: "", status: "Active", notes: "" };

export const TrackerDialog = ({ open, item, onClose, onSave }) => {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(item ? { ...BLANK, ...item, renewal_date: item.renewal_date || "", monthly_revenue: String(item.monthly_revenue ?? "") } : BLANK);
      setErrors({}); setServerError("");
    }
  }, [open, item]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.domain.trim()) errs.domain = "Domain is required";
    const rev = form.monthly_revenue === "" ? 0 : Number(form.monthly_revenue);
    if (Number.isNaN(rev) || rev < 0) errs.monthly_revenue = "Enter a positive number";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await onSave({
        domain: form.domain.trim(), registrar: form.registrar, renewal_date: form.renewal_date || null,
        hosting: form.hosting, monthly_revenue: rev, status: form.status, notes: form.notes,
      });
    } catch (err) {
      const fe = fieldErrorsFrom422(err.response?.data?.detail);
      if (Object.keys(fe).length) setErrors(fe);
      else setServerError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tracker-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto border-white/10 bg-[#0D111A]">
        <DialogHeader><DialogTitle className="text-white">{item ? "Edit tracked site" : "Track a website"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <FormField id="tracker-domain" label="Domain" error={errors.domain}>
            <input id="tracker-domain" data-testid="tracker-input-domain" value={form.domain} onChange={set("domain")} placeholder="example.com" className={adminInput(errors.domain)} />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id="tracker-registrar" label="Registrar">
              <input id="tracker-registrar" data-testid="tracker-input-registrar" value={form.registrar} onChange={set("registrar")} className={adminInput(false)} />
            </FormField>
            <FormField id="tracker-renewal" label="Renewal date" error={errors.renewal_date}>
              <input id="tracker-renewal" type="date" data-testid="tracker-input-renewal-date" value={form.renewal_date} onChange={set("renewal_date")} className={adminInput(errors.renewal_date)} />
            </FormField>
            <FormField id="tracker-hosting" label="Hosting">
              <input id="tracker-hosting" data-testid="tracker-input-hosting" value={form.hosting} onChange={set("hosting")} className={adminInput(false)} />
            </FormField>
            <FormField id="tracker-revenue" label="Monthly revenue (USD)" error={errors.monthly_revenue}>
              <input id="tracker-revenue" type="number" min="0" step="0.01" data-testid="tracker-input-monthly-revenue" value={form.monthly_revenue} onChange={set("monthly_revenue")} className={adminInput(errors.monthly_revenue)} />
            </FormField>
          </div>
          <FormField id="tracker-status" label="Status">
            <select id="tracker-status" data-testid="tracker-input-status" value={form.status} onChange={set("status")} className={adminInput(false)}>
              {TRACKER_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </FormField>
          <FormField id="tracker-notes" label="Notes">
            <textarea id="tracker-notes" rows={3} data-testid="tracker-input-notes" value={form.notes} onChange={set("notes")} className={adminInput(false)} />
          </FormField>
          {serverError && <p data-testid="tracker-server-error" className="text-sm text-red-400">{serverError}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} data-testid="tracker-cancel-button" className="rounded-full border border-white/10 px-5 py-2 text-sm text-slate-300 hover:bg-white/5">Cancel</button>
            <button type="submit" disabled={busy} data-testid="tracker-save-button" className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
